import { quizData } from '../data/quizData'

/**
 * Lead scoring weights — assigns points to each answer tag.
 * Higher score = hotter lead (more buyer intent + better fit).
 */
const TAG_WEIGHTS: Record<string, number> = {
  // ====== ROLE FIT (Q1) ======
  PM: 10,
  DIRECTOR: 10,
  CONSULTANT: 8,
  ENTREPRENEUR: 8,
  TECH: 6,
  STUDENT: 3,

  // ====== COUNTRY (Q2) — LATAM target ======
  MEX: 2,
  COL: 2,
  ARG: 2,
  CHI: 2,
  PER: 2,
  ECU: 2,
  OTRO: 1,

  // ====== EXPERIENCE (Q3) ======
  ADVANCED: 8,
  EXPERIENCED: 8,
  INTERMEDIATE: 6,
  JUNIOR: 4,
  BEGINNER: 2,

  // ====== TIME LOST / PAIN (Q4) ======
  EXTREME_PAIN: 15,
  HIGH_PAIN: 12,
  UNAWARE_PAIN: 8,
  MED_PAIN: 7,
  LOW_PAIN: 2,

  // ====== FRUSTRATION (Q5) — any frustration = signal ======
  DOC_PAIN: 5,
  REPORT_PAIN: 5,
  TEAM_PAIN: 4,
  MEETING_PAIN: 4,
  AI_PAIN: 6,
  VALUE_PAIN: 5,

  // ====== OBSOLESCENCE FEAR (Q6) ======
  FEAR_HIGH: 12,
  FEAR_AWAKENED: 10,
  FEAR_MED: 8,
  FEAR_LOW: 3,
  FEAR_NONE: 0,

  // ====== DOCUMENTATION STRUGGLE (Q7) ======
  DOC_CHAOS: 8,
  DOC_HARD: 6,
  DOC_OK: 4,
  DOC_GOOD: 2,
  DOC_OPTIMIZE: 2,

  // ====== AI USAGE (Q8) — gap = opportunity ======
  AI_BEGINNER: 6,
  AI_CURIOUS: 6,
  AI_AWARE: 5,
  AI_CASUAL: 4,
  AI_POWER_USER: 2,

  // ====== TOOLS KNOWN (Q9, multi-select) ======
  TOOL_CHATGPT: 1,
  TOOL_NOTION: 1,
  TOOL_NOTEBOOK: 1,
  TOOL_MIRO: 1,
  TOOL_JIRA: 1,
  TOOL_ZAPIER: 1,
  TOOL_POWERBI: 1,
  TOOL_NONE: 3,

  // ====== AUTOMATION WISH (Q10) — any = signal ======
  AUTO_DOC: 4,
  AUTO_KPI: 4,
  AUTO_TASKS: 4,
  AUTO_RISK: 4,
  AUTO_COMM: 4,
  AUTO_RETRO: 4,

  // ====== PROMPT ENGINEERING (Q11) ======
  PROMPT_NONE: 8,
  PROMPT_HEARD: 7,
  PROMPT_CURIOUS: 6,
  PROMPT_BASIC: 6,
  PROMPT_EXPERT: 2,

  // ====== CERTIFICATION DESIRE (Q12) ======
  CERT_EUROPE: 15,
  CERT_HIGH: 12,
  CERT_MED: 8,
  CERT_AWAKENED: 6,
  CERT_CONDITIONAL: 4,

  // ====== TIME RECOVERY POTENTIAL (Q13) ======
  TIME_10: 5,
  TIME_5_10: 4,
  TIME_2_5: 2,
  TIME_2: 1,

  // ====== INVESTMENT READINESS (Q14) — strong buyer signal ======
  INVEST_HIGH: 20,
  INVEST_MED: 15,
  INVEST_LOW: 5,
  INVEST_NONE: -10,

  // ====== URGENCY (Q15) — strong buyer signal ======
  URGENCY_NOW: 20,
  URGENCY_SOON: 15,
  URGENCY_LATER: 5,
  URGENCY_NONE: -5,
}

const MAX_POSSIBLE_SCORE = 140 // sum of top weights; we normalize to 0-100

export type LeadTier = 'fire' | 'hot' | 'warm' | 'cold'

export interface LeadScoreResult {
  rawScore: number
  score: number // normalized 0-100
  tier: LeadTier
  tierLabel: string
  profile: string
  tags: string[]
  breakdown: { tag: string; points: number; questionIndex: number }[]
}

export function computeLeadScore(
  answers: Record<number, string | string[]>,
): LeadScoreResult {
  const breakdown: { tag: string; points: number; questionIndex: number }[] = []
  const collectedTags: string[] = []
  let rawScore = 0

  Object.entries(answers).forEach(([qIndexStr, answer]) => {
    const qIndex = Number(qIndexStr)
    const tagList = Array.isArray(answer) ? answer : [answer]

    tagList.forEach((tag) => {
      const points = TAG_WEIGHTS[tag] ?? 0
      rawScore += points
      collectedTags.push(tag)
      breakdown.push({ tag, points, questionIndex: qIndex })
    })
  })

  // Normalize to 0-100
  const score = Math.max(
    0,
    Math.min(100, Math.round((rawScore / MAX_POSSIBLE_SCORE) * 100)),
  )

  // Tier
  let tier: LeadTier
  let tierLabel: string
  if (score >= 80) {
    tier = 'fire'
    tierLabel = '🔥 FIRE — Lead muy caliente, listo para comprar'
  } else if (score >= 60) {
    tier = 'hot'
    tierLabel = '🟠 HOT — Lead caliente, alta intención'
  } else if (score >= 40) {
    tier = 'warm'
    tierLabel = '🟡 WARM — Lead tibio, requiere nurturing'
  } else {
    tier = 'cold'
    tierLabel = '🔵 COLD — Lead frío, baja intención'
  }

  // Derive profile label
  const profile = deriveProfile(collectedTags, score)

  return {
    rawScore,
    score,
    tier,
    tierLabel,
    profile,
    tags: collectedTags,
    breakdown,
  }
}

function deriveProfile(tags: string[], score: number): string {
  const has = (t: string) => tags.includes(t)

  if (has('PM') || has('DIRECTOR')) {
    if (score >= 70)
      return 'Project Manager Listo para Transformación Inmediata'
    if (score >= 50) return 'Project Manager en Transición Activa'
    return 'Project Manager Explorando Opciones'
  }
  if (has('CONSULTANT')) return 'Consultor Buscando Diferenciación con IA'
  if (has('ENTREPRENEUR')) return 'Emprendedor Optimizando Operaciones'
  if (has('TECH')) return 'Profesional Tech Migrando a Gestión con IA'
  if (has('STUDENT')) return 'Profesional Junior Construyendo Carrera con IA'

  return 'Profesional Explorando IA para Proyectos'
}
